import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../utils/redux/userSlice";
import UserForm from "../components/UserComponents/UserForm/UserForm.component";
import UserGrid from "../components/UserComponents/UserGrid/UserGrid.component";
import { User } from "../utils/models/User";
import { deleteUser, getAllUsers, newUser } from "../utils/services/user.service";
import { getAllCompanies } from "../utils/services/company.service";
import { Company } from "../utils/models/Company";
import ModalComponent from "../components/ModalCompnent/modal.component";
import { AdminUser, UserAdminRoles, UserLanguages, UserRoles } from "../utils/const";
import { hash } from "bcryptjs";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IOutletContext } from "../utils/interfaces/IOutletContext";
import { IToastProps, NotificationToast } from "aw-notificationtoast";
import  { ButtonsLine } from "aw-management-buttons-line";
import { ISelectOptions } from "aw-combobox";
import { ROLES } from "aw-consts";
import { useTheme } from "../utils/contexts/ThemeContext";
import { SearchPanel } from "aw-search-panel";

const UsersPage = () => {
    const [isNew, setIsNew] = useState(false)
    const [loading, setLoading] = useState(false)
    const [showModal, setShowModal] = useState(false)
    const [toast, setToast] = useState<IToastProps>({ showToast: false, msg: '', fail: false })
    const [newEditDelObj, setNewEditDelObj] = useState(new User())
    const [users, setUsers] = useState<User[]>([])
    const [formLock, setFormLock] = useState(true)

    const [allCompanies, setAllCompanies] = useState<Company[]>([])
    const [allCompaniesObj, setAllCompaniesObj] = useState<ISelectOptions[]>([])
    const [selectedCompanies, setSelectedCompanies] = useState<ISelectOptions>()

    const [allRolesObj] = useState<ISelectOptions[]>(UserAdminRoles())
    const [selectedRol, setSelectedRol] = useState<ISelectOptions>()

    const [allLanguagesObj] = useState<ISelectOptions[]>(UserLanguages())
    const [selectedLanguage, setSelectedLanguage] = useState<ISelectOptions>()
    const [active, setActive] = useState(true)
    const context: IOutletContext = useOutletContext();

    const user = useSelector(selectUser)
    const { t } = useTranslation();

    const roles = user.role == ROLES.Demo ? UserRoles() : UserAdminRoles()
    const [rolesOptions] = useState<ISelectOptions[]>(roles)
    const [roleSelected, setRoleSelected] = useState<ISelectOptions>(roles[0])

    const theme = useTheme()

    useEffect(() => {
        context.setState(t('Users'))
    })

    useEffect(() => {
        getAllUsersAux();
    }, [roleSelected])

    useEffect(() => {
        getAllUsersAux();
        getAllCompaniesAux();
    }, [])

    const showToastTimeOut = (msg: string, failToast: boolean) => {
        setToast({ showToast: true, msg: msg, fail: failToast })
        setTimeout(() => setToast({ showToast: false, msg: msg, fail: failToast }), 2000)
    }

    const newUserClick = () => {
        if (user.role === ROLES.Demo) {
            showToastTimeOut(t('noCreatePermissions'), true)
        } else {
            setSelectedCompanies(allCompaniesObj[0])
            setSelectedRol(allRolesObj[0])
            setSelectedLanguage(allLanguagesObj[0])
            setFormLock(false)
            setIsNew(true)
            setActive(true)
        }

    }

    const getAllUsersAux = () => {
        setLoading(true)
        getAllUsers(user.token, Number(roleSelected.value)).then((json) => {
            setUsers(json)
        }).catch(() => {
            showToastTimeOut(t('ErrorLoad'), true)
        }).finally(() => { setTimeout(() => { setLoading(false) }, 500) })
    }

    const getAllCompaniesAux = () => {
        getAllCompanies(user.token).then((json) => {
            setAllCompanies(json)
            const companiesObj = json.map((c: Company) => ({ label: c.name, value: c.idcompany }))
            setAllCompaniesObj(companiesObj)
        }).catch((e) => {
            console.log('getAllCompanies Error', e);
        })
    }

    const validateEmail = (email: string) => {
        const regex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
        return regex.test(email);
    }

    const saveClick = async () => {
        if (user.role === ROLES.Demo) {
            showToastTimeOut(t('noEditPermissions'), true);
            return;
        }
        if (!selectedCompanies || !selectedLanguage || !selectedRol) {
            showToastTimeOut(t('RequireParams'), true);
            return;
        }
        const newCompany = allCompanies.find((c) => c.idcompany === Number(selectedCompanies.value));
        // Create a copy to avoid direct mutation
        const updatedUser = { ...newEditDelObj };
        updatedUser.active = active;
        updatedUser.language = Number(selectedLanguage.value);
        updatedUser.user_rol = Number(selectedRol.value);
        updatedUser.company = newCompany ? newCompany : new User().company;
        if (
            updatedUser.password &&
            updatedUser.password.length > 0 &&
            updatedUser.name &&
            updatedUser.name.length > 0 &&
            validateEmail(updatedUser.email)
        ) {
            try {
                updatedUser.password = await hash(updatedUser.password, 10);
                const json = await newUser(user.token, updatedUser);
                let c = [];
                let msg = t('CreatedUserOK');
                if (updatedUser.iduser > 0) {
                    msg = t('UpdatedUserOK');
                    const newUsers = users.filter((user) => user.iduser !== json.iduser);
                    c = [...newUsers, json];
                } else {
                    c = [...users, json];
                }
                setUsers(c);
                setIsNew(false);
                setNewEditDelObj(new User());
                showToastTimeOut(msg, false);
            } catch (error) {
                setIsNew(false);
                setNewEditDelObj(new User());
                showToastTimeOut(t('UserError'), true);
            }
        } else {
            showToastTimeOut(t('RequireParams'), true);
        }
    }

    const cancelClick = () => {
        setIsNew(false)
        setNewEditDelObj(new User())
    }

    const editClick = (u: User) => {
        if (u.company) { setSelectedCompanies({ label: u.company.name, value: u.company.idcompany }) }
        setSelectedRol(allRolesObj.find((r) => (r.value === u.user_rol)))
        setSelectedLanguage(allLanguagesObj.find((l) => (l.value === u.language)))
        setFormLock(true)
        setIsNew(true)
        setNewEditDelObj(u)
        setActive(u.active)
    }

    const deleteClick = (u: User) => {
        if (user.role === ROLES.Demo) {
            showToastTimeOut(t('noDeletePermissions'), true)
        }
        else if (u.email !== AdminUser) {
            setShowModal(true)
            setNewEditDelObj(u)
        } else {
            showToastTimeOut(t('NoDelAdmin'), true)
        }
    }

    const DesBloquearClick = () => {
        setFormLock(!formLock)
    }

    const onFormChange = (event: any) => {
        const { name, value } = event.target;
        if (name === 'active') {
            setActive(!active)

        } else {
            setNewEditDelObj((data: any) => ({
                ...data,
                [name]: value
            }));
        }
    }

    const ModalNo = () => {
        setShowModal(false)
    }

    const ModalYes = () => {
        setShowModal(false)

        deleteUser(user.token, newEditDelObj.iduser).then((response) => {
            if (response.status >= 200 && response.status < 300) {
                const newUsers = users.filter((user) => user !== newEditDelObj);
                setUsers(newUsers);
                setNewEditDelObj(new User())
                showToastTimeOut(t('DeleteUserOK'), false)
            } else { throw new Error() }
        }).catch(() => {
            setNewEditDelObj(new User())
            showToastTimeOut(t('DeleteUserError'), true)
        })
    }

    const [query, setQuery] = useState<string>('');
    return (
        <>
            <NotificationToast showToast={toast.showToast} msg={toast.msg} fail={toast.fail} />
            <ModalComponent showModal={showModal} textDel={`${t('QuestionDelete')} ${newEditDelObj.name + ' ' + newEditDelObj.surname}?`} onClickNo={ModalNo} onClickYes={ModalYes} />
            <div className='row border-0 ms-2 me-2' style={{ maxWidth: '100%' }}>
                <div className='col-12 col-md-12 p-1'>
                    <ButtonsLine comboOptions={rolesOptions} onChangeSelected={(e: ISelectOptions) => setRoleSelected(e)} selectedOption={roleSelected} edit={isNew} viewMode={false}
                        formLock={formLock} onClickNew={newUserClick} onClickSave={saveClick} onClickCancel={cancelClick} onClickDesBloquear={DesBloquearClick} comboOptions2={[]} onChangeSelected2={() => { return null }} selectedOption2={null}
                        comboName="" comboName2="" classNameDanger="btn-Custom btn-IDRed" classNamePrimary="btn-Custom btn-Grey" classNameSuccess="btn-Custom btn-IDGreen" classNameNeutral="btn-Simple"
                        containerColor="var(--background-secondary)" rounded="rounded-1" dark={theme.theme === 'dark' ?  true : false} saveText={t('Save')}>
                            <SearchPanel searchPlaceholder={t('Search')} searchAction={(query) => setQuery(query)} options={[]} value={[]} onChange={function (value: (string | number)[]): void {
                            throw new Error("Function not implemented.");
                        } }/>
                        </ButtonsLine>
                </div>
            </div>
            <div className='row border-0 ms-2 me-2' style={{ maxWidth: '100%' }}>
                <div className='col-12 col-md-12 p-1'>
                    {isNew ? <UserForm edit={isNew} userAux={newEditDelObj} sendChange={onFormChange}
                        formLock={formLock} selectedCompany={selectedCompanies} onChangeSelectCompany={setSelectedCompanies} optionsCompany={allCompaniesObj}
                        selectedUserRol={selectedRol} onChangeSelectUserRol={setSelectedRol} optionsUserRol={allRolesObj}
                        selectedUserLang={selectedLanguage} onChangeSelectUserLang={setSelectedLanguage} optionsUserLang={allLanguagesObj} />
                        : <UserGrid userRole={user.role} users={users} onEditClick={editClick} onDeleteClick={deleteClick} loading={loading} query={query} setQuery={setQuery} />}
                </div>
            </div>
        </>
    )
}

export default UsersPage